/**
 * Skeleton Components
 *
 * Reusable skeleton components for loading states across the application.
 * Designed to match the visual layout of actual content for smooth transitions.
 */

import { Skeleton } from "~/core/components/ui/skeleton";
import { cn } from "~/core/lib/utils";

/**
 * StyleGrid Skeleton - Home page style grid loading state
 */
export function StyleGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid w-full grid-cols-2 gap-[5px] border-b border-black/10 bg-white lg:grid-cols-3">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="relative aspect-3/4 overflow-hidden bg-[#F3F3F1]"
        >
          <Skeleton className="h-full w-full rounded-none" />
        </div>
      ))}
    </div>
  );
}

/**
 * Home page hero section skeleton
 */
export function HeroSkeleton() {
  return (
    <div className="relative flex min-h-[23vh] w-full flex-col justify-center overflow-hidden border-b border-black/10 px-4 md:min-h-[34vh] md:px-12">
      <Skeleton className="h-[12vw] w-[60%] md:h-[10vw]" />
      <div className="mt-[5px] flex w-full flex-row items-end justify-between gap-4 pb-6 md:mt-4">
        <div className="max-w-[50%] space-y-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-36" />
        </div>
        <div className="flex items-center gap-1.5 md:gap-2">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Table Skeleton - For credits, payments, admin tables
 */
export function TableSkeleton({
  rows = 5,
  columns = 5,
  showHeader = true,
  className,
}: {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("overflow-x-auto rounded-xl border border-gray-100", className)}>
      <table className="w-full">
        {showHeader && (
          <thead className="border-b border-gray-100 bg-gray-50/50">
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} className="px-4 py-4 md:px-6">
                  <Skeleton className="h-3 w-16" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-4 py-4 md:px-6">
                  <Skeleton
                    className={cn(
                      "h-4",
                      colIndex === 0 ? "w-24" : "w-16"
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Page Header Skeleton
 */
export function PageHeaderSkeleton({
  showButton = false,
  showSubtitle = true,
}: {
  showButton?: boolean;
  showSubtitle?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <Skeleton className="h-6 w-40" />
        {showSubtitle && <Skeleton className="mt-2 h-4 w-56" />}
      </div>
      {showButton && <Skeleton className="h-10 w-36 rounded-xl" />}
    </div>
  );
}

/**
 * Filter Chips Skeleton
 */
export function FilterChipsSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-20 rounded-lg" />
      ))}
    </div>
  );
}

/**
 * Credits Page Skeleton
 */
export function CreditsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton showButton showSubtitle />
      <FilterChipsSkeleton count={6} />
      <TableSkeleton rows={8} columns={5} />
    </div>
  );
}

/**
 * Payments Page Skeleton
 */
export function PaymentsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton showButton={false} showSubtitle />
      <TableSkeleton rows={8} columns={6} />
    </div>
  );
}

/**
 * Admin Members Skeleton
 */
export function AdminMembersSkeleton() {
  return (
    <div className="animate-[fadeIn_0.3s] p-10">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col items-start justify-between gap-4 border-b border-gray-200 bg-gray-50/50 p-6 md:flex-row md:items-center">
          <Skeleton className="h-11 w-72 rounded-xl" />
          <Skeleton className="h-5 w-32" />
        </div>
        <TableSkeleton rows={8} columns={5} showHeader className="border-0 rounded-none" />
      </div>
    </div>
  );
}

/**
 * Admin Payments Skeleton
 */
export function AdminPaymentsSkeleton() {
  return (
    <div className="animate-[fadeIn_0.3s] p-10">
      {/* Info banner skeleton */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-28 rounded-lg" />
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50/50 p-6">
          <Skeleton className="h-5 w-48" />
        </div>
        <TableSkeleton rows={8} columns={8} showHeader className="border-0 rounded-none" />
      </div>
    </div>
  );
}

/**
 * Admin Logs Skeleton
 */
export function AdminLogsSkeleton() {
  return (
    <div className="animate-[fadeIn_0.3s] p-10">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col items-start justify-between gap-4 border-b border-gray-200 bg-gray-50/50 p-6 md:flex-row md:items-center">
          <div className="flex gap-2">
            <Skeleton className="h-11 w-32 rounded-xl" />
            <Skeleton className="h-11 w-32 rounded-xl" />
          </div>
          <Skeleton className="h-5 w-32" />
        </div>
        <TableSkeleton rows={10} columns={6} showHeader className="border-0 rounded-none" />
      </div>
    </div>
  );
}

/**
 * Style Detail Modal Skeleton
 */
export function StyleDetailSkeleton() {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center md:p-4">
      <div className="relative flex h-[90vh] w-full flex-col overflow-hidden bg-white md:h-auto md:max-h-[90vh] md:max-w-lg md:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="space-y-1">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>

        {/* Sample Gallery */}
        <div className="p-6">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-24 flex-shrink-0 rounded-lg" />
            ))}
          </div>
        </div>

        {/* Upload Section */}
        <div className="flex-1 space-y-4 p-6">
          <div className="flex h-48 items-center justify-center rounded-xl border-2 border-dashed border-gray-200">
            <div className="text-center">
              <Skeleton className="mx-auto h-12 w-12 rounded-full" />
              <Skeleton className="mx-auto mt-4 h-4 w-32" />
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="border-t border-gray-100 p-6">
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/**
 * Account Page Skeleton
 */
export function AccountPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="rounded-xl border border-gray-100 bg-white p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      </div>

      {/* Settings Cards */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-gray-100 bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-4 w-44" />
            </div>
            <Skeleton className="h-9 w-20 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Full Page Skeleton - Home
 */
export function HomePageSkeleton() {
  return (
    <div className="relative z-10 flex w-full grow flex-col">
      <HeroSkeleton />
      <StyleGridSkeleton count={9} />
    </div>
  );
}
